import React, { useState, useRef, useCallback, useEffect } from 'react';

const WaitingParticipantsPanel = ({
  waitingParticipants = [],
  onAdmit,
  onAdmitAll,
  onDeny,
  onClose,
  onSendMessage,
  chatMessages = {},       // { [odId]: [messages] }
  rightSidebarCollapsed = true
}) => {
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const hasUserDragged = useRef(false);

  // Per-participant chat state
  const [openChatFor, setOpenChatFor] = useState(null); // odId of participant whose chat is open
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);

  // Calculate initial position
  useEffect(() => {
    if (!hasUserDragged.current && panelRef.current) {
      const sidebarWidth = rightSidebarCollapsed
        ? 56
        : (window.innerWidth >= 768 ? 320 : 288);
      const panelWidth = 320;
      const gap = 8;
      setPosition({
        x: window.innerWidth - sidebarWidth - panelWidth - gap,
        y: 64,
      });
    }
  }, [rightSidebarCollapsed]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
    hasUserDragged.current = true;
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 100;
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    const touch = e.touches[0];
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
    setIsDragging(true);
    hasUserDragged.current = true;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.current.x;
    const newY = touch.clientY - dragOffset.current.y;
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 100;
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // Auto-scroll chat when messages update for the open chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, openChatFor]);

  const handleSendChat = () => {
    const text = chatInput.trim();
    if (!text || !openChatFor || !onSendMessage) return;
    onSendMessage(openChatFor, text);
    setChatInput('');
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  if (waitingParticipants.length === 0) {
    return null;
  }

  const openParticipantMessages = openChatFor ? (chatMessages[openChatFor] || []) : [];
  const getUnreadCount = (odId) => {
    const msgs = chatMessages[odId] || [];
    return msgs.filter((m) => m.senderRole === 'participant').length;
  };

  return (
    <div
      ref={panelRef}
      className="fixed w-80 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-slideIn"
      style={{
        left: position ? `${position.x}px` : undefined,
        top: position ? `${position.y}px` : undefined,
        userSelect: isDragging ? 'none' : undefined,
        zIndex: 2147483647,
      }}
    >
      {/* Header - drag handle */}
      <div
        className="bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] px-4 py-3 flex items-center justify-between"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
              {waitingParticipants.length}
            </span>
          </div>
          <span className="text-white font-semibold">
            {openChatFor ? 'Chat' : 'Waiting Room'}
          </span>
          <svg className="w-4 h-4 text-white/40 ml-1" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="4" cy="4" r="1.5" /><circle cx="4" cy="8" r="1.5" /><circle cx="4" cy="12" r="1.5" />
            <circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="8" r="1.5" /><circle cx="10" cy="12" r="1.5" />
          </svg>
        </div>
        <div className="flex items-center gap-1">
          {openChatFor && (
            <button
              onClick={() => { setOpenChatFor(null); setChatInput(''); }}
              className="text-white/70 hover:text-white transition-colors mr-1"
              title="Back to list"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Chat View for a specific participant */}
      {openChatFor ? (
        <div className="flex flex-col" style={{ height: '320px' }}>
          {/* Chat participant header */}
          <div className="px-4 py-2 border-b border-white/10 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center text-white text-xs font-semibold">
              {waitingParticipants.find((p) => p.odId === openChatFor)?.odName?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <span className="text-white text-sm font-medium">
              {waitingParticipants.find((p) => p.odId === openChatFor)?.odName || 'Participant'}
            </span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {openParticipantMessages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-white/30 text-xs">No messages yet</p>
              </div>
            )}
            {openParticipantMessages.map((msg) => {
              const isHost = msg.senderRole === 'host';
              return (
                <div key={msg.id} className={`flex ${isHost ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-xl px-3 py-1.5 ${
                    isHost
                      ? 'bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] text-white'
                      : 'bg-white/10 text-white'
                  }`}>
                    {!isHost && (
                      <p className="text-[10px] font-medium text-purple-300 mb-0.5">{msg.senderName}</p>
                    )}
                    <p className="text-xs break-words">{msg.message}</p>
                    <p className={`text-[9px] mt-0.5 ${isHost ? 'text-white/60' : 'text-white/40'}`}>
                      {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2 border-t border-white/10">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder="Reply..."
                className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#6a1fbf]/50 transition-colors"
              />
              <button
                onClick={handleSendChat}
                disabled={!chatInput.trim()}
                className="p-1.5 bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] rounded-lg text-white hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Admit All Button */}
          {waitingParticipants.length > 1 && onAdmitAll && (
            <div className="px-4 py-2 border-b border-white/10">
              <button
                onClick={onAdmitAll}
                className="w-full py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm font-medium transition-colors"
              >
                Admit All ({waitingParticipants.length})
              </button>
            </div>
          )}

          {/* Participant List */}
          <div className="max-h-64 overflow-y-auto">
            {waitingParticipants.map((participant) => {
              const unread = getUnreadCount(participant.odId);
              return (
                <div
                  key={participant.odId}
                  className="px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center text-white font-semibold overflow-hidden">
                        {participant.odAvatar ? (
                          <img
                            src={participant.odAvatar}
                            alt={participant.odName}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          participant.odName?.charAt(0)?.toUpperCase() || '?'
                        )}
                      </div>
                      {/* Name */}
                      <div>
                        <p className="text-white font-medium text-sm">{participant.odName || 'Unknown'}</p>
                        <p className="text-gray-400 text-xs">Waiting to join...</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1.5">
                      {/* Chat button */}
                      {onSendMessage && (
                        <button
                          onClick={() => setOpenChatFor(participant.odId)}
                          className="p-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors relative"
                          title="Chat"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          {unread > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">
                              {unread > 9 ? '9+' : unread}
                            </span>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => onAdmit && onAdmit(participant.odId)}
                        className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                        title="Admit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeny && onDeny(participant.odId)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        title="Deny"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default WaitingParticipantsPanel;
